import { LeftOutlined } from '@ant-design/icons';
import { useResolutionInfo } from '@mm/hooks';
import { useViewActions } from '@mm/state/actions/viewActions';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import classNames from 'classnames';
import { includes } from 'lodash';
import React, { useEffect } from 'react';
import useRouter from 'use-react-router';
import { GreenMarker } from '../../../common/components/GreenMarker/GreenMarker';
import { CHATBOT_EDITOR } from '../../../common/constants/routes';
import { trackWebAppGAEvent } from '../../../common/helpers/plugins/website-google-analytics';
import { useGreenCueMarkers } from '../../../common/hooks/use-green-cue-markers';
import { isMatchingChatStarterEditPath } from '../../../editor/features/beta/modules/ChatStarters/pathBuilder';
import { MIN_HEADER_SCREEN_WIDTH } from '../../../editor/pages/ChatbotEditorPage/ChatbotEditor';
import { FollowConvosItem } from '../ActivePageSlideMenu/ActivePageSlideMenu';
import { GuardedProLinkClick } from '../BotBuilderSidebar';
import TopLevelSidebarLink from '../common/TopLevelSidebarLink/TopLevelSidebarLink';
import { NavbarDropdown } from '../NavbarDropdown/NavbarDropdown';
import {
  ChatBotSidebarTitle,
  SidebarMenuChatStartersMenuItem,
  SidebarMenuDefinition,
  SidebarTitleDefinition,
} from '../sidebar-menu';
import './SidebarMenuGroup.pcss';
import SidebarMenuItem from './SidebarMenuItem/SidebarMenuItem';

type Props = {
  menuGroup: SidebarTitleDefinition;
  toggleMenuGroupExpanded: (menuGroupId: string) => void;
  expandedMenuGroups: string[];
  guardedProLinkClick: GuardedProLinkClick;
  sidebarMinimized: boolean;
  disableMenuItems: boolean;
  activeChatbotId?: number;
  onMobileClick?: () => void;
  facebookPage?: EnhancedBillingPage;
  hidden?: boolean;
  followUpConvos?: Record<number, FollowConvosItem | undefined>;
  isFreeTrialActive?: boolean;
  isAutomationsEnabled?: boolean;
  isMobile?: boolean;
  isBeta?: boolean;
};

const getIsActiveMenuItem = (
  pathname: string,
  menuItem: SidebarMenuDefinition,
) => {
  // custom rule for chat starters as edit screens are not nested under its path
  if (
    menuItem.id === SidebarMenuChatStartersMenuItem.id &&
    isMatchingChatStarterEditPath(pathname)
  ) {
    return true;
  }

  const childRoutes = menuItem.childRoutes ? menuItem.childRoutes : [];
  const routes = [menuItem.url, ...childRoutes];
  return routes.some(route => pathname.indexOf(route) > -1);
};

// tslint:disable-next-line: cyclomatic-complexity
export const SidebarMenuGroup = (props: Props) => {
  const {
    menuGroup,
    expandedMenuGroups,
    guardedProLinkClick,
    sidebarMinimized,
    activeChatbotId,
    onMobileClick,
    disableMenuItems,
    facebookPage,
    toggleMenuGroupExpanded,
    hidden,
    followUpConvos,
    isFreeTrialActive,
    isAutomationsEnabled,
    isMobile,
    isBeta,
  } = props;
  const { history, location } = useRouter();
  const { pathname } = location;
  const { screenWidth } = useResolutionInfo();
  const groupName = menuGroup.text.toLocaleLowerCase();

  const { id, menuItems, icon, text, url, featureFlag } = menuGroup;
  const baseUrl = `${CHATBOT_EDITOR}/${activeChatbotId}`;

  let isExpanded = includes(expandedMenuGroups, id);
  let showMenuItems = Boolean(
    menuItems.length && (sidebarMinimized || isExpanded) && !disableMenuItems,
  );
  if (
    isMatchingChatStarterEditPath(pathname) &&
    ChatBotSidebarTitle.id === id
  ) {
    showMenuItems = true;
    isExpanded = true;
  }
  const {
    areAllChatStartersActivated,
    areAllCampaignsActivated,
    isZapierActivated,
  } = useGreenCueMarkers();
  const matchActiveTabRegExp = new RegExp(`${baseUrl}\\${url}(\\/|$).*`);
  const isActiveGroupLink = !!url && matchActiveTabRegExp.test(pathname);
  const isActive = isExpanded || isActiveGroupLink;

  const navbarDropdownItems = menuItems.map(
    ({ name, url, featureFlag, icon, description }) => ({
      name,
      href: baseUrl + url,
      requiredFeature: featureFlag,
      icon,
      description,
    }),
  );

  const viewActions = useViewActions();
  // effect to store computed menu group state in redux
  useEffect(() => {
    if (isActive && showMenuItems) {
      viewActions.showBetaSubNav(groupName);
    }
    return () => {
      // close on unmount
      viewActions.hideBetaSubNav(groupName);
    };
  }, [showMenuItems, isActive]);

  const onTitleClick = (isExpanded: boolean, disableMenuItems: boolean) => {
    trackWebAppGAEvent('click', {
      category: 'navigation',
      label: `navbar_link_${groupName}`,
    });

    const hasActiveMenuItem = menuGroup.menuItems.some(menuItem => {
      return getIsActiveMenuItem(location.pathname, menuItem);
    });
    // Only toggle closed if no expanded child
    if (isExpanded) {
      if (!hasActiveMenuItem) {
        toggleMenuGroupExpanded(menuGroup.id);
      }
    } else {
      toggleMenuGroupExpanded(menuGroup.id);
    }

    if (menuGroup.url && activeChatbotId) {
      const routeUrl = `${CHATBOT_EDITOR}/${activeChatbotId}${menuGroup.url}`;

      if (onMobileClick) {
        if (disableMenuItems || !menuGroup.menuItems.length) {
          history.push(routeUrl);
          onMobileClick();
        }
      } else {
        history.push(routeUrl);
      }
    }
  };

  const menuItemsClassName = classNames('sidebar-menu-group__menu-items', {
    'sidebar-menu-group__menu-items--hidden': hidden,
    'sidebar-menu-group__menu-items--with-automations-and-free-trial':
      isFreeTrialActive && isAutomationsEnabled,
  });

  const menuGroupClassName = classNames(
    'sidebar-menu-group',
    `${groupName}-item`,
  );

  const isMenuGroupSelected =
    location.pathname.indexOf(menuGroup.url || 'someplaceholder') > -1;
  const showGreenMarkerForChatStarters =
    menuGroup.id === ChatBotSidebarTitle.id && !areAllChatStartersActivated;
  const showGreenMarkerForCampaigns =
    menuGroup.id === 'CAMPAIGNS' &&
    !areAllCampaignsActivated &&
    location.pathname.indexOf('drip-campaign-detail') === -1;
  const showGreenMarkerForIntegrations =
    !isZapierActivated && menuGroup.id === 'INTEGRATIONS';

  const showGreenMarker =
    !isMenuGroupSelected &&
    (showGreenMarkerForChatStarters ||
      showGreenMarkerForCampaigns ||
      showGreenMarkerForIntegrations);

  return (
    <div className={menuGroupClassName}>
      {screenWidth > MIN_HEADER_SCREEN_WIDTH && menuItems.length ? (
        <div className="sidebar-menu-group__container">
          {isBeta && <div className="sidebar-menu-group__beta">beta</div>}
          {showGreenMarker && <GreenMarker />}
          <NavbarDropdown
            title={text}
            items={navbarDropdownItems}
            url={menuGroup.url ? baseUrl + menuGroup.url : undefined}
            isActive={isActive}
            requiredFeature={featureFlag}
          />
        </div>
      ) : (
        <div
          className={classNames('sidebar-menu-group__container', {
            'sidebar-menu-group__container-mobile': isMobile,
          })}
        >
          {showGreenMarker && (
            <GreenMarker className="sidebar-menu-group__container--green-marker" />
          )}
          <TopLevelSidebarLink
            activeFacebookPage={facebookPage}
            onClick={() => onTitleClick(isExpanded, disableMenuItems)}
            isActive={isActive}
            text={text}
            followUpCount={
              id === 'INBOX' && followUpConvos ? followUpConvos : undefined
            }
            requiredFeature={featureFlag}
            withRightArrow={!!(menuItems.length && isMobile)}
            isBeta={isBeta}
            className={
              isMobile
                ? 'sidebar-menu-group__top-level-sidebar-link__mobile'
                : ''
            }
          />
        </div>
      )}

      {showMenuItems && (
        <div className={menuItemsClassName}>
          {isMobile && (
            <>
              <div
                className="sidebar-menu-group__back-menu"
                onClick={() => toggleMenuGroupExpanded(menuGroup.id)}
              >
                <LeftOutlined className="sidebar-menu-group__left-arrow" />
                Main menu
              </div>
              <div className="sidebar-menu-group__menu-items-header">
                {menuGroup.text}
              </div>
            </>
          )}

          {menuItems.map(menuItem => (
            <SidebarMenuItem
              key={menuItem.id}
              menuItem={menuItem}
              baseUrl={baseUrl}
              isActive={getIsActiveMenuItem(pathname, menuItem)}
              guardedProLinkClick={guardedProLinkClick}
              onMobileClick={onMobileClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};
